import { useContext, useEffect, useMemo } from 'react';
import {
  Button,
  CircularProgress,
  Icon,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowDown,
  ArrowUp,
  Bike,
  CookingPot,
  ListChecks,
  MessagesSquareIcon,
} from 'lucide-react';
import { addMinutes, format } from 'date-fns';
import DataCell from '@/components/DataCell';
import { OrderStatus } from '@/types';
import { useAppDispatch, useAppSelector, useOrder, useUserProfile } from '@/hooks';
import { WebsiteContext } from '@/pages/website/WebsiteProvider';

const steps = [
  {
    id: OrderStatus.REVIEWING,
    title: 'Revisando',
    icon: <ListChecks width={40} height={40} color="white" strokeWidth={1} />,
  },
  {
    id: OrderStatus.PREPARING,
    title: 'Preparando',
    icon: <CookingPot width={40} height={40} color="white" strokeWidth={1} />,
  },
  {
    id: OrderStatus.SENDING,
    title: 'Enviando',
    icon: <Bike width={40} height={40} color="white" strokeWidth={1} />,
  },
];

const StatusView = () => {
    const { palette } = useTheme();
    const dispatch = useAppDispatch();
    const { organization } = useContext(WebsiteContext);
    const order = useAppSelector((state) => state.order);
    const { profile } = useUserProfile(order?.client);
    const { subscribeToOrderByOrganizationId } = useOrder();
    
    const activeStep = useMemo(() => {
        return steps.findIndex((s) => s.id === order.status) ?? 0;
    }, [order.status]);
    
    const customerData = useMemo(() => {
        if (profile) {
            return profile;
        }

        if (order?.client) {
            return order.client;
        }

        return null;
    }, [order.client, profile]);
    
    const helpMessage = async () => {
        if (!organization?.whatsapp) return;
    
        const urlencodedtext = `%20Necesito%20ayuda%20con%20mi%20pedido%20No. ${order.number}`;
    
        window.open(
          `https://wa.me/${organization.whatsapp}?text=${urlencodedtext}`
        );
    };

    useEffect(() => {
        if (!organization?.id) return;
        const unsubscribe = subscribeToOrderByOrganizationId(organization.id);
        return () => {
          unsubscribe();
        };
      }, [dispatch, organization?.id, subscribeToOrderByOrganizationId]);
    
    return (
        <Stack height={'100%'} sx={{ backgroundColor: '#DDEAFD' }}>
            <Paper square>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    spacing={2}
                    p={2}
                >
                    <Typography variant="h4"> Estatus del Pedido </Typography>
                    <Typography variant="h3"> No. {order.number} </Typography>
                </Stack>
            </Paper>
    
            <Stack flex={1} p={2} spacing={2}>
                <Stack direction={'row'} spacing={2}>
                    <Paper sx={{ width: '100%', p: 2, alignContent: 'center' }}>
                    <Stack
                        direction={'row'}
                        spacing={2}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Icon>
                        <ArrowUp color="#BDBDBD" />
                        </Icon>
                        <Typography variant="h4">
                        {order?.createdAt
                            ? format(new Date(order.createdAt), 'p')
                            : 'N/A'}
                        </Typography>
                    </Stack>
                    </Paper>
                    <Paper sx={{ width: '100%', p: 2, alignContent: 'center' }}>
                    <Stack
                        direction={'row'}
                        spacing={2}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                    >
                        <Icon>
                        <ArrowDown color="#BDBDBD" />
                        </Icon>
                        <Typography variant="h4">
                        {order?.createdAt
                            ? format(addMinutes(order?.createdAt, 60), 'p')
                            : 'N/A'}
                        </Typography>
                    </Stack>
                    </Paper>
                </Stack>
        
                <Stack>
                    <Paper>
                    <Stack
                        height={'125px'}
                        sx={{
                        backgroundColor: palette.primary.main,
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        position: 'relative',
                        }}
                    >
                        <CircularProgress
                        size={90}
                        thickness={1}
                        sx={{
                            color: 'white',
                            opacity: 0.5,
                            position: 'absolute',
                            zIndex: 100,
                            top: '50%',
                            left: '50%',
                            marginLeft: '-45px',
                            marginTop: '-45px',
                            animationDuration: '8s',
        
                            '& .MuiCircularProgress-circle': {
                            animationDuration: '8s !important',
                            animationTimingFunction: 'linear !important',
                            },
                        }}
                        />
                        {steps[activeStep]?.icon ? (
                        <Icon
                            sx={{
                            height: 40,
                            width: 40,
                            zIndex: 100,
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginLeft: '-20px',
                            marginTop: '-20px',
                            }}
                        >
                            {steps[activeStep]?.icon}
                        </Icon>
                        ) : null}
                    </Stack>
        
                    <Stack spacing={2} px={1} py={2}>
                        <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((step) => {
                            const stepProps: { completed?: boolean } = {};
                            const labelProps: {
                            optional?: React.ReactNode;
                            } = {};
        
                            return (
                            <Step
                                key={step.id}
                                {...stepProps}
                                sx={{
                                '& .MuiStepLabel-iconContainer .Mui-completed': {
                                    color: palette.success.main,
                                },
                                }}
                            >
                                <StepLabel {...labelProps}>{step.title}</StepLabel>
                            </Step>
                            );
                        })}
                        </Stepper>
                    </Stack>
                    </Paper>
                </Stack>
        
                <Stack spacing={2}>
                    {customerData?.location?.neighborhood &&
                    customerData?.location?.house ? (
                    <Paper sx={{ width: '100%', p: 2, alignContent: 'center' }}>
                        <DataCell
                        vertical
                        label="Dirección de entrega"
                        value={`${customerData.location.neighborhood} ${customerData.location.house}`}
                        />
                    </Paper>
                    ) : null}
                    {customerData?.location?.address ? (
                    <Paper sx={{ width: '100%', p: 2, alignContent: 'center' }}>
                        <DataCell
                        vertical
                        label="Instrucciones de llegada"
                        value={customerData.location.address ?? ''}
                        />
                    </Paper>
                    ) : null}
                </Stack>
            </Stack>
            <Stack p={2} justifyContent={'center'} alignItems={'center'}>
                <Button
                    size="large"
                    color="success"
                    startIcon={<MessagesSquareIcon />}
                    sx={{ textTransform: 'uppercase' }}
                    onClick={helpMessage}
                >
                    Ayuda
                </Button>
            </Stack>
        </Stack>
    )
}

export default StatusView;
